import { RadioButtonWithLabelOption, RadioLayoutType, TextFieldWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';
import { useCallback, useMemo } from 'react';
import { style } from 'typestyle';

import { SessionServerSelectors } from '../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../codegen/Localize';
import { FormikTextArea, FormikTextBox } from '../../../../commonInfra/components/FormikField';
import { FormikRadioButtonGroup } from '../../../../commonInfra/components/FormikRadioButtonGroup';
import { textInputLabelCellStyle } from '../../../../commonInfra/styles/CommonStyles';
import { EmailValidationHelper } from '../../../../ts/helpers/EmailValidationHelper';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { wideField } from '../../styles/SiteNameAndIdStyles';
import { useSelector } from '../../utils';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';
import { SiteSettingsForServerAdminProps } from '../site-settings/ReactSiteSettingsForServerAdmin';

const SETTING_CONTENT_SUBSCRIPTION_EMAIL_ENABLED = 'settings.customSubscriptionEmailEnabled';
const SETTING_CONTENT_SUBSCRIPTION_FOOTER_ENABLED = 'settings.customSubscriptionFooterEnabled';
const inputRadioButtonLabelStyle = style({
    $nest: {
      '> label': {
        flexBasis: '300px'
      }
    }
  });


export type EmailSettingsSectionProps = SiteSettingsForServerAdminProps<'customSubscriptionEmailEnabled' |
                                                                        'customSubscriptionEmail' |
                                                                        'customSubscriptionFooterEnabled' |
                                                                        'customSubscriptionFooter'>;

export function EmailSettingsSection(props: EmailSettingsSectionProps) {
  const formik = useFormikContext<EmailSettingsSectionProps>();
  const defaultSubscriptionEmail = useSelector(SessionServerSelectors.defaultSubscriptionsEmail) ?? '';
  const buttonsList = useMemo(getButtonsListEmail, []);
  const buttonsListFooter = useMemo(getButtonsListFooter, []);
  const validateCustomEmail = useCallback((emailAddress: string) => !EmailValidationHelper.isValidEmail(emailAddress) ? Messages.customEmailInvalid() : undefined, []);
  // Hook to trigger validation when component renders
  // TODO: TFS1213609: Move the manual trigger to common FormikTextField so it could be leveraged in other settings sections. After that, MRs 7582 and 7566 (in that order) can be reverted.
  // When I tried validation trigger from FormikTextField, karma runner fails with error "Disconnectedreconnect failed before timeout of" even when the product works. Looks browser becomes unresponsive (in unit tests only) for some reason.
  React.useEffect(
    () => {
      if(formik.values.settings.customSubscriptionEmailEnabled) {
        formik.setFieldTouched('settings.customSubscriptionEmail', true, true);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.settings.customSubscriptionEmailEnabled, formik.setFieldTouched]
  );

  return <SettingsSection title={Messages.emailSettings()} description={Messages.emailSettingsDescription()}>
      <table>
        <tbody>
          <tr>
            <td className={textInputLabelCellStyle} id='email-from-address-label'> {Messages.emailFromAddress_colon()} </td>
            <td className={wideField}>
              {
                !formik.values.settings.customSubscriptionEmailEnabled
                ? renderDefaultSubscriptionEmailTextBox()
                : renderCustomSubscriptionEmailTextBox()
              }
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              {
                renderRadioButtonGroup(Messages.customAddress(), 'radio-button-group-email-address', buttonsList, SETTING_CONTENT_SUBSCRIPTION_EMAIL_ENABLED)
              }
            </td>
          </tr>
          <tr>
            <td className={textInputLabelCellStyle} id='email-footer-label'> {Messages.emailFooter_colon()} </td>
            <td className={wideField}>
              {
                renderCustomSubscriptionFooterTextArea()
              }
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              {
                renderRadioButtonGroup(Messages.customFooter(), 'radio-button-group-email-footer', buttonsListFooter, SETTING_CONTENT_SUBSCRIPTION_FOOTER_ENABLED)
              }
            </td>
          </tr>
        </tbody>
      </table>
  </SettingsSection>;

  function getButtonsListEmail(): RadioButtonWithLabelOption<boolean>[] {
    return [
      {
        value: false,
        label: Messages.defaultAddress(),
        testId: 'email-address-default-radio-button'
      },
      {
        value: true,
        label: Messages.customAddress(),
        testId: 'email-address-custom-radio-button'
      }
    ];
  }

  function getButtonsListFooter(): RadioButtonWithLabelOption<boolean>[] {
    return [
      {
        value: false,
        label: Messages.none(),
        testId: 'email-footer-default-radio-button'
      },
      {
        value: true,
        label: Messages.customFooter(),
        testId: 'email-footer-custom-radio-button'
      }
    ];
  }

  function renderRadioButtonGroup(label: string, testId: string, buttonList: RadioButtonWithLabelOption<boolean>[], settingName: string){
    return <FormikRadioButtonGroup
              aria-label={label}
              name={settingName}
              className={inputRadioButtonLabelStyle}
              testId={testId}
              buttonList={buttonList}
              layout={RadioLayoutType.Horizontal}
              justifyHorizontalItems={true} />;
  }

  function renderCustomSubscriptionEmailTextBox(): React.ReactNode {
    return <FormikTextBox
              name='settings.customSubscriptionEmail'
              label=''
              className={wideField}
              aria-labelledby='email-from-address-label'
              testId='email-from-address-input'
              validate={validateCustomEmail}
              disabled={!props.settings.customSubscriptionEmailEnabled && !formik.values.settings.customSubscriptionEmailEnabled}
              maxLength={100} />;
  }

  function renderDefaultSubscriptionEmailTextBox(): React.ReactNode {
    return <TextFieldWidget
              value={defaultSubscriptionEmail}
              className={wideField}
              label=''
              aria-labelledby='email-from-address-label'
              testId='default-email-from-address-input'
              disabled={true} />;
  }

  function renderCustomSubscriptionFooterTextArea(){
    return <FormikTextArea
              name={'settings.customSubscriptionFooter'}
              testId='email-footer-input'
              className={wideField}
              label=''
              aria-labelledby='email-footer-label'
              rows={4}
              disabled={!props.settings.customSubscriptionFooterEnabled && !formik.values.settings.customSubscriptionFooterEnabled} />;
  }
}


/**
 * Specs for registering the component in the settings registry
 */
export const EmailSettingsSpecs = componentRegistryEntry({
  name: 'EmailSettingsSection',
  component: EmailSettingsSection
});
